import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { Options, Requisition } from 'models'

import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'

import useSubmitTask from 'hooks/useSubmitTask'
import {
	ApproveRelapseClinicalSchema,
	ExtendedRelapseClinicalApprovalSchema,
	RelapseClinicalApprovalSchema,
} from './RelapseClinicalApprovalSchema'
import useSession from 'hooks/useSession'
import HFTextInput from 'components/HFInputs/HFTextInput'
import ExpandableWidget from 'components/ExpandableWidget/ExpandableWidget'
import TaskNotesController from '../../TaskSubControllers/TaskNotesController/TaskNotesController'
import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import AlertBox from 'components/AlertBox/AlertBox'
import InfoList from 'components/InfoList/InfoList'
import { mapRelapseClinicalApprovalDetails } from 'controllers/TaskManager/TaskSubControllers/TaskDetailsControllers/detailsUtils'

import styles from '../Dialog.module.sass'
import TaskMenuController from '../TaskMenu/TaskMenuController'
import { useNoWaitFetchList } from 'store'
import { TaskNeedsController } from 'controllers/TaskManager/TaskSubControllers/TaskNeedsController/TaskNeedsController'
import TaskRefusalController from 'controllers/TaskManager/TaskSubControllers/TaskRefusalController/TaskRefusalController'
import PersonalAccidentWarning from 'controllers/TaskManager/TaskSubControllers/PersonalAccidentWarning'

const RelapseClinicalApprovalTaskController = (props) => {
	const { i18n } = useGlobal()
	const { data, resolve, dismiss, id } = props
	const [defaultValues, setDefaultValues] = useState(null)
	const [argValues, setArgValues] = useState(null)
	const [submissionType, setSubmissionType] = useState(null)
	const history = useHistory()
	const { user: sessionUser } = useSession()

	const IS_ADMINISTRATIVE = sessionUser?.roles.includes('manager')

	const schema = IS_ADMINISTRATIVE
		? submissionType === 'approve'
			? ApproveRelapseClinicalSchema({ ...argValues })
			: ExtendedRelapseClinicalApprovalSchema({ ...argValues })
		: RelapseClinicalApprovalSchema({ ...argValues })

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(schema),
		shouldUnregister: true,
	})
	const { handleSubmit, formState, watch, getValues, reset } = methods

	const {
		error,
		submitting,
		resolveTask,
		approveTask,
		requestApprovalTask,
		requestManagerApprovalTask,
	} = useSubmitTask(data?.id, formState?.errors)

	const [specialtyId] = watch(['specialtyId'])

	const { contents } = useNoWaitFetchList(
		[
			`clinical_processes/${data?.processId}/requisitions/${data?.requisitionId}/notes`,
			`clinical_processes/${data.processId}/requisitions/${data.requisitionId}`,
			!IS_ADMINISTRATIVE && !!specialtyId
				? `clinical_processes/${data.processId}/requisitions/${data.requisitionId}/specialties/${specialtyId}/service_providers`
				: null,
		],
		[
			(json) => {
				return json.notes
			},
			Requisition.fromJSON,
			Options.fromSimpleObjectJSON,
			!IS_ADMINISTRATIVE && !!specialtyId
				? Options.fromSimpleObjectJSON
				: null,
		]
	)

	const [notesContents, requisitionContents, serviceProvidersContents] =
		contents

	const { contents: notes, state: notesState } =
		notesContents && notesContents.state === 'hasValue'
			? notesContents
			: { state: notesContents?.state }

	const { contents: requisition, state: requisitionState } =
		requisitionContents && requisitionContents.state === 'hasValue'
			? requisitionContents
			: { state: requisitionContents?.state }

	const { contents: serviceProviders } =
		serviceProvidersContents &&
		serviceProvidersContents.state === 'hasValue'
			? serviceProvidersContents
			: { state: serviceProvidersContents?.state }

	const isLoading = requisitionState === 'loading'
	const isNotesLoading = notesState === 'loading'

	useEffect(() => {
		if (
			!isLoading &&
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && reset(data.formState ? data.formState : defaultValues)
		}
	}, [
		reset,
		defaultValues,
		isLoading,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		if (!isLoading) {
			let defaultValues = {}
			if (requisition) {
				setArgValues({
					requestEscort: requisition?.requestEscort,
					requiresChargeAmount: data?.requiresChargeAmount,
					establishmentId: requisition?.establishment?.id,
				})
			}

			if (requisition?.requestEscort && IS_ADMINISTRATIVE) {
				defaultValues = {
					...defaultValues,
					'needsOptions.housing': false,
					'needsOptions.transport': false,
				}
			}

			if (IS_ADMINISTRATIVE) {
				defaultValues = {
					...defaultValues,
					administrativeObservation:
						requisition?.administrativeObservation || '',
				}
			}

			defaultValues = {
				...defaultValues,
				needEscort: false,
			}

			if (requisition?.notes) {
				defaultValues = {
					...defaultValues,
					notes: requisition?.notes || '',
				}
			}

			if (serviceProviders) {
				const establishmentId =
					requisition?.establishment?.id.toString()
				const hasEstablishment = serviceProviders.some(
					(provider) => provider.value === establishmentId
				)

				if (hasEstablishment) {
					defaultValues = {
						providerId: establishmentId,
						...defaultValues,
					}
				}
			}

			setDefaultValues({ ...defaultValues, changeProvider: false })
		}
	}, [isLoading, requisition ?? {}, serviceProviders ?? []])

	const onSubmit = (formData) => {
		let finalData = {}

		const {
			changeProvider,
			specialtyId,
			providerId,
			notes,
			...remainingData
		} = formData
		finalData = remainingData

		if (!isNaN(specialtyId)) {
			finalData = { ...finalData, specialtyId }
		}
		if (!isNaN(providerId)) {
			finalData = { ...finalData, providerId }
		}

		if (submissionType === 'resolve') {
			resolveTask(finalData)
				.then(() => resolve())
				.catch(() => {})
		} else if (submissionType === 'approve') {
			approveTask(finalData)
				.then(() => resolve())
				.catch(() => {})
		}
	}

	if (isLoading && !formState.isDirty) {
		return <LoadingIndicator />
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	const details = mapRelapseClinicalApprovalDetails(requisition, i18n)
	const isFromIsland = data?.injured?.isFromIsland

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
					submissionType={submissionType}
				/>
			)}
			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<TaskHeaderController
						requisition={requisition}
						task={data}
						hideSpecialtyAndAppointment
					/>

					<div className={styles['notes-form-container']}>
						<TaskNotesController
							data={{
								id: data.id,
								processId: data.processId,
								taskNote: notes,
							}}
							requisition={requisition}
							isNotesLoading={isNotesLoading}
						/>
					</div>

					<form
						id={`form_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}

						<TaskRefusalController requisition={requisition} />

						<PersonalAccidentWarning task={data} />

						{!IS_ADMINISTRATIVE &&
							requisition?.administrativeObservation && (
								<InfoList
									items={[
										{
											title: i18n`Administrative Observations`,
											text: requisition.administrativeObservation,
											icon: 'observation',
										},
									]}
								/>
							)}

						<InfoList items={details} />

						{isFromIsland && (
							<TaskNeedsController
								isAdministrative={IS_ADMINISTRATIVE}
								requisition={requisition}
							/>
						)}

						<fieldset className={styles['observation-container']}>
							{IS_ADMINISTRATIVE ? (
								<ExpandableWidget
									className={styles['observation']}
									heading='h5'
									title={i18n`Administrative Observations`}
								>
									<HFTextInput
										name='administrativeObservation'
										placeholder={i18n`Administrative observation (optional)`}
										multiline={true}
										rows={5}
										noResize
									/>
								</ExpandableWidget>
							) : (
								<ExpandableWidget
									className={styles['observation']}
									heading='h5'
									title={i18n`Clinical Observations`}
								>
									<HFTextInput
										name='medicalObservation'
										placeholder={i18n`Clinical observation (optional)`}
										multiline={true}
										rows={5}
										noResize
									/>
								</ExpandableWidget>
							)}
						</fieldset>
					</form>
				</div>
			</FormProvider>

			{IS_ADMINISTRATIVE ? (
				<TaskMenuController
					data={{
						formId: id,
						options: ['postpone', 'forward', 'refuse'],
						submitting,
						setSubmissionType,
						submissionType: 'resolve',
						requestApprovalTask,
						requestManagerApprovalTask,
						getValues,
					}}
				/>
			) : (
				<TaskMenuController
					data={{
						formId: id,
						options: ['forward', 'refuse'],
						history,
						submitting,
						setSubmissionType,
						submissionType: 'approve',
						getValues,
					}}
				/>
			)}
		</>
	)
}

export default RelapseClinicalApprovalTaskController
